import { type Registry as Services, service } from '@ember/service';
import Model, { type AsyncBelongsTo, attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { Hotelinfo, Ota } from 'frontend-utils/models';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type {
  OtaId,
  Platform,
} from 'frontend-utils/utils/constants/constant-values';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';
import type {
  OtaHotelInfo,
  ParityScreenshotParity,
  Period,
} from 'lumen/models';
import type HotelAndMarketService from 'lumen/services/hotel-and-market';
import {
  type MembershipType,
  POSITION_TO_BASERATE,
  type PositionToBaserate,
} from 'lumen/utils/constants/general-constants';
import {
  ISSUE_TYPES,
  type IssueType,
} from 'lumen/utils/constants/pi-constant-values';

const ISSUE_TYPES_WITH_SCREENSHOTS: IssueType[] = [
  ISSUE_TYPES.LOSS_SOLDOUT,
  ISSUE_TYPES.LOSS_ROOMTYPE,
  ISSUE_TYPES.LOSS_PRICE_DIFFERENCE,
  ISSUE_TYPES.LOSS_DIFFERENT_CANCELLATION,
];

type Message = 'general.missing' | 'rates.soldout' | '';

export default class Parity extends Model {
  [Type] = 'parity' as const;

  @belongsTo('hotelinfo', { async: false, inverse: null })
  declare hotelinfo: Hotelinfo;

  @belongsTo('ota', { async: false, inverse: null })
  declare ota: Ota;

  @belongsTo('ota-hotel-info', { async: true, inverse: null })
  declare otaHotelInfo: AsyncBelongsTo<OtaHotelInfo>;

  @belongsTo('period', { async: false, inverse: null })
  declare period: Period;

  @attr('boolean') declare bestFlex: boolean;
  @attr('string') declare channel: OtaId;
  @attr('number') declare cityTax: number;
  @attr('boolean') declare cityTaxIncl: boolean;
  @attr('string') declare currency: SupportedCurrencies;
  @attr('number') declare differenceWithBaserate: number;
  @attr('dayjs') declare extractDatetime: DayjsTransformReturn;
  @attr('boolean') declare isBaserate: boolean;
  @attr('boolean') declare isOutOfSync: boolean;
  @attr('number') declare issueType: IssueType;
  @attr('boolean') declare isWholesaleRate: boolean;
  @attr('number') declare maxPersons: number;
  @attr('number') declare mealtypeIncluded: number;
  @attr('number') declare membershiptype: MembershipType;
  @attr('string') declare message: Message;
  @attr('number') declare otherTaxes: number;
  @attr('boolean') declare otherTaxesIncl: boolean;
  @attr('number') declare platform: Platform;
  @attr('string') declare pos: string;
  @attr('number') declare positionToBaserate: PositionToBaserate;
  @attr() declare rateCode: string;
  @attr('string') declare roomName: string;
  @attr('string') declare roomType: string;
  @attr('string') declare screenshotParityId: string;
  @attr('boolean') declare shopCityTaxIncl: boolean;
  @attr('string') declare shopCurrency: string;
  @attr('boolean') declare shopOtherTaxesIncl: boolean;
  // Tax attributes
  @attr('number') declare shopValue: number;
  @attr('boolean') declare shopVatIncl: boolean;
  @attr('number') declare value: number;
  @attr('number') declare vat: number;
  @attr('boolean') declare vatIncl: boolean;

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketService;

  @service declare intl: Services['intl'];
  @service('ota') declare otaService: Services['ota'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get brandRate() {
    const missingBrandRates: PositionToBaserate[] = [
      POSITION_TO_BASERATE.NO_DATA,
      POSITION_TO_BASERATE.LOSS_SOLDOUT,
      POSITION_TO_BASERATE.WIN_SOLDOUT,
    ];

    if (missingBrandRates.includes(this.positionToBaserate)) {
      return 0;
    }

    return this.value - this.differenceWithBaserate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get differenceWithBaserateClass() {
    if (this.isWinIssue) {
      return 'text-success-default';
    }
    if (this.isLossIssue) {
      return 'text-critical-default';
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedArrivalDate() {
    return this.period.day.date.format('L');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedBrandRate() {
    if (this.positionToBaserate === POSITION_TO_BASERATE.NO_DATA) {
      return this.intl.t('general.missing');
    }

    if (this.positionToBaserate === POSITION_TO_BASERATE.LOSS_SOLDOUT) {
      return this.intl.t('rates.soldout');
    }

    if (this.positionToBaserate === POSITION_TO_BASERATE.WIN_SOLDOUT) {
      return this.intl.t('general.availability');
    }

    return this.hotelAndMarketService.formatRate(this.brandRate);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChannel() {
    return this.otaService.otaIdToPrettyName(this.channel);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChannelRate() {
    if (this.isMissing) {
      return this.intl.t('general.missing');
    }

    if (
      this.isSoldout ||
      this.positionToBaserate === POSITION_TO_BASERATE.WIN_SOLDOUT
    ) {
      return this.intl.t('general.soldout');
    }

    return this.hotelAndMarketService.formatRate(this.value);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDifferenceWithBaserate() {
    if (this.isMissing || this.isSoldout) {
      return this.intl.t('general.missing');
    }

    return this.hotelAndMarketService.formatRate(
      this.differenceWithBaserate,
      true,
      true,
      { maximumFractionDigits: 0, minimumFractionDigits: 0 },
      true,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedScreenshotParity() {
    if (!this.isScreenshotSupported) {
      return this.intl.t('general.not_applicable');
    }
    if (this.screenshotParity) {
      return this.intl.t('general.screenshot');
    }
    // Show missing if no matching screenshot is found
    return this.intl.t('general.missing');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedSource() {
    if (this.isDirectlyShopped) {
      return this.intl.t('parity_analytics.directly_shopped');
    }
    return this.otaService.otaIdToPrettyName(this.ota.name);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNoData() {
    return this.positionToBaserate === POSITION_TO_BASERATE.NO_DATA;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDirectlyShopped() {
    return this.isMajorOta && this.channel === this.ota.id;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLossIssue() {
    return (
      this.positionToBaserate === POSITION_TO_BASERATE.LOSS_PRICE_DIFFERENCE ||
      this.positionToBaserate === POSITION_TO_BASERATE.LOSS_SOLDOUT
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMajorOta() {
    return !this.isWholesaleRate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMeetIssue() {
    return this.positionToBaserate === POSITION_TO_BASERATE.MEET;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMissing() {
    return this.message === 'general.missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isScreenshotSupported() {
    const supportedScreenshotPlatforms =
      this.otaHotelInfo.get('supportedScreenshotPlatforms') ?? [];
    const isPlatformSupportedForCurrentSubscriptionAndOTA =
      supportedScreenshotPlatforms.includes(this.platform);
    const isIssueTypeSupported = ISSUE_TYPES_WITH_SCREENSHOTS.includes(
      this.issueType,
    );

    return (
      isPlatformSupportedForCurrentSubscriptionAndOTA &&
      this.isLossIssue &&
      isIssueTypeSupported
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSoldout() {
    return this.message === 'rates.soldout';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isWinIssue() {
    return (
      this.positionToBaserate === POSITION_TO_BASERATE.WIN_PRICE_DIFFERENCE ||
      this.positionToBaserate === POSITION_TO_BASERATE.WIN_SOLDOUT
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get screenshotParity() {
    // Getter instead of @belongsTo because screenshotParity is not always available
    return this.store.peekRecord<ParityScreenshotParity>(
      'parity/screenshot-parity',
      this.screenshotParityId,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get screenshotUrl() {
    if (this.screenshotParity) {
      return this.screenshotParity.screenshotUrl;
    }
    return null;
  }

  get sortValue(): number {
    if (this.screenshotParity) {
      return 2;
    }
    if (!this.isScreenshotSupported) {
      return 1;
    }
    return 0; // Missing screenshots
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get unixArrivalDate() {
    return this.period.day.date.unix();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  toJSON() {
    return this.serialize();
  }
}
